import React, { useEffect, useState } from "react";
import InspectionDBStatus from "../../../../../../models/indexedDB/inspection-db-status.entity";
import InspectionStepLocalDB from "../../../../../../models/indexedDB/inspection-step-local-db.entity";
import { SystemFlagsEnum } from "../../../../../../models/session/system-flags.enum";
import IndexedDBService from "../../../../../../services/indexedDB/IndexedDB.service";
import inspectionUploadService from "../../../../services/inspection-upload.service";
import InspectionPanelListNative from "./InspectionPanelList.native";
import storageService from "../../../../../../services/storage.service";
import appletSubgroupService from "../../../../services/applet-subgroup.service";
import { useHistory } from "react-router-dom";
import inspectionProgressService from "../../../../services/inspection-progress.service";

const InspectionPanelListContainer = () => {
  const history = useHistory();

  const [waitingInspections, setWaitingInspection] = React.useState<any>({});
  const [sentInspections, setSentInspection] = React.useState<any>({});
  const [errorInspections, setErrorInspectios] = React.useState<any>({});
  const [incompleteInspections, setIncompleteInspections] = React.useState<any>({});

  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false);
  const [showCleanSentDialog, setShowCleanSentDialog] = useState<boolean>(false);

  const [loading, setLoading] = React.useState<boolean>(true);
  const [inspectionToDelete, setInspectionToDelete] = React.useState<string>('');

  const openDeleteDialog = (inspectinoIdentifier: string) => {
    setInspectionToDelete(inspectinoIdentifier);
    setShowDeleteDialog(true);
  }

  const groupBy = (list: InspectionStepLocalDB[]) => {
    const map: any = {};

    list.forEach((inspection: InspectionStepLocalDB) => {
      const identifier = inspection.uniqueIdentifier;
      if (!map[identifier]) map[identifier] = [];
      map[identifier].push(inspection);
    });

    return map;
  }

  const filterByStatus = (steps: InspectionStepLocalDB[], status: string) => steps
    .filter((step: InspectionStepLocalDB) => step.status === status);

  const loadInspections = () => {
    setLoading(true);
    IndexedDBService
      .getAll()
      .then((result: any) => {
        const allSteps: InspectionStepLocalDB[] = result
          .map((item: any) => new InspectionStepLocalDB(item));
        const groups = groupBy(allSteps);

        const waiting: any = {};
        const sent: any = {};
        const errors: any = {};
        const incomplete: any = {};

        Object.keys(groups).forEach((identifier: string) => {
          const steps: InspectionStepLocalDB[] = groups[identifier];
          const errorSteps = filterByStatus(steps, InspectionDBStatus.WITH_ERROR);

          if (errorSteps.length) {
            errors[identifier] = errorSteps;
          } else if (inspectionProgressService.isIncomplete(steps)) {
            incomplete[identifier] = steps;
            return;
          }

          const waitingSteps = filterByStatus(steps, InspectionDBStatus.WAITING);
          const sentSteps = filterByStatus(steps, InspectionDBStatus.SENT);

          if (waitingSteps.length) waiting[identifier] = waitingSteps;
          if (sentSteps.length) sent[identifier] = sentSteps;
        });

        setWaitingInspection(waiting);
        setSentInspection(sent);
        setErrorInspectios(errors);
        setIncompleteInspections(incomplete);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const deleteInspection = async () => {
    const toRemove: InspectionStepLocalDB[] =
      waitingInspections[inspectionToDelete]
      || errorInspections[inspectionToDelete]
      || incompleteInspections[inspectionToDelete];

    if (toRemove) {
      await Promise.all(
        toRemove.map((item: InspectionStepLocalDB) => IndexedDBService.removeData(item.id))
      );
    }

    setShowDeleteDialog(false);
    setInspectionToDelete('');

    loadInspections();
  }

  const sendPhotosToServer = () => {
    const isConnect = window.navigator.onLine;
    const isUploadingPhotos = JSON
      .parse(localStorage.getItem(SystemFlagsEnum.IS_UPLOADING_PHOTO) || '0');

    if (!isConnect) return;

    if (!isUploadingPhotos && isConnect) {
      inspectionUploadService.getAllAwaitingPhotos();
    }
  }

  const refreshSession = () => {
    const data = storageService.getObject('company');

    if (!data) {
      history.push('/');
      return;
    }

    const session = storageService.getObject('session');
    const appletProfileId = session?.company?.profiles?.[0]?.applet_profile_id || '';

    if (!appletProfileId) return;

    appletSubgroupService.refreshAppletSubgroup(data.id, appletProfileId)
      .then((result: any) => {
        const carSubgroups = result?.data?.Carro;
        if (!Array.isArray(carSubgroups)) return;

        const freshActiveSubgroups = carSubgroups
          .filter((item: any) => item.active)
          .map((item: any) => ({ ...item, attachments: item.attachment || [] }))
          .sort((a: any, b: any) => a.order_number - b.order_number);

        const atualSession = storageService.getObject('session');
        const subgroupTypes = atualSession?.applet_subgroup_types;
        if (!Array.isArray(subgroupTypes)) return;

        const carType = subgroupTypes.find((item: any) => item.title === 'Carro');
        const profile = carType?.profiles?.find((p: any) => p.id === appletProfileId);
        if (!profile) return;

        profile.applet_subgroups = freshActiveSubgroups;

        storageService.setObject('session', atualSession);
      })
      .catch(() => { });
  }

  const checkRefreshAppletSubgroups = () => {
    const sessionDate: any = new Date(storageService.getObject('update-session'));
    const dateNow: any = new Date();

    const diff = Math.abs(dateNow - sessionDate);

    if (diff > 1800000) {
      refreshSession();
    }
  }

  useEffect(() => {
    setLoading(true);

    const handleRefreshList = () => {
      loadInspections();
    };

    IndexedDBService.createdDB().then(() => {
      loadInspections();
      sendPhotosToServer();
    });

    document.addEventListener('refreshList', handleRefreshList);
    checkRefreshAppletSubgroups();

    return () => {
      document.removeEventListener('refreshList', handleRefreshList);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if(waitingInspections) {
      sendPhotosToServer()
    }
  }, [waitingInspections]);

  return (
    <InspectionPanelListNative
      waitingInspections={waitingInspections}
      sentInspections={sentInspections}
      errorInspections={errorInspections}
      incompleteInspections={incompleteInspections}
      showDeleteDialog={showDeleteDialog}
      setShowDeleteDialog={setShowDeleteDialog}
      openDeleteDialog={openDeleteDialog}
      deleteInspection={deleteInspection}
      showCleanSentDialog={showCleanSentDialog}
      setShowCleanSentDialog={setShowCleanSentDialog}
      loading={loading}
    />
  )
};

export default InspectionPanelListContainer;
